import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import {
  copyText,
  getErrorMessage,
  rowsToTsv,
  TableColumn,
} from '../utils/reviewHelpers';

export type AdminTableRow = Record<string, any>;
export type AdminTableState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
};

export interface AdminTableColumn extends TableColumn<AdminTableRow> {
  displayValue?: (row: AdminTableRow) => React.ReactNode;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
}

const humanize = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const automaticColumns = (rows: AdminTableRow[]): AdminTableColumn[] =>
  rows.length
    ? Object.keys(rows[0]).map((key) => ({ key, label: humanize(key) }))
    : [];

const defaultValue = (key: string, value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') {
    return (
      <Chip
        size="small"
        label={value ? 'Yes' : 'No'}
        color={value ? 'success' : 'default'}
        variant={value ? 'filled' : 'outlined'}
      />
    );
  }
  if (key === 'status' || key === 'last_action' || key === 'action') {
    return <Chip size="small" label={String(value)} variant="outlined" />;
  }
  return String(value);
};

const AdminDataTable = ({
  name,
  rows,
  state,
  columns: suppliedColumns,
  onDeactivate,
  onEdit,
  pendingKey,
}: {
  name: string;
  rows: AdminTableRow[];
  state: AdminTableState;
  columns?: AdminTableColumn[];
  onDeactivate?: (row: AdminTableRow) => void;
  onEdit?: (row: AdminTableRow) => void;
  pendingKey?: string | null;
}) => {
  const columns = suppliedColumns || automaticColumns(rows);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const copyTable = async () => {
    try {
      await copyText(rowsToTsv(rows, columns));
      setCopyFeedback('Copied table to clipboard.');
    } catch (error) {
      setCopyFeedback(getErrorMessage(error, 'Unable to copy table.'));
    }
  };

  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />
        <Typography color="text.secondary">
          Loading {name.toLowerCase()}…
        </Typography>
      </Stack>
    );
  }
  if (state.status === 'error') {
    return (
      <Alert severity="error">
        {state.error || `Unable to load ${name.toLowerCase()}.`}
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ userSelect: 'text' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentCopy />}
          onClick={copyTable}
          disabled={!columns.length || !rows.length}
          aria-label={`Copy ${name} table`}
        >
          Copy table
        </Button>
        <Typography variant="caption" color="text.secondary" aria-live="polite">
          {copyFeedback || `${rows.length} record${rows.length === 1 ? '' : 's'}`}
        </Typography>
      </Stack>

      {!rows.length ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No records.
        </Typography>
      ) : (
        <TableContainer
          sx={{
            maxHeight: 520,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: 'background.paper',
          }}
        >
          <Table stickyHeader size="small" aria-label={name}>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={String(column.key)}
                    align={column.align || 'left'}
                    sx={{
                      minWidth: column.minWidth,
                      py: 1.5,
                      fontWeight: 700,
                      color: 'text.primary',
                      backgroundColor: '#f1f5f9',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
                {(onDeactivate || onEdit) && (
                  <TableCell
                    sx={{
                      py: 1.5,
                      fontWeight: 700,
                      backgroundColor: '#f1f5f9',
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                    }}
                  >
                    Actions
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const rowKey = String(
                  row.assignment_id ||
                    row.company_id ||
                    row.event_id ||
                    `${name}-${index}`
                );
                const rowPending = pendingKey === rowKey;
                return (
                  <TableRow
                    key={rowKey}
                    hover
                    sx={{
                      '&:nth-of-type(even)': { backgroundColor: '#f8fafc' },
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    {columns.map((column) => {
                      const key = String(column.key);
                      const raw = column.rawValue
                        ? column.rawValue(row)
                        : row[key];
                      return (
                        <TableCell
                          key={key}
                          align={column.align || 'left'}
                          sx={{
                            py: 1.25,
                            verticalAlign: 'top',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {column.displayValue
                            ? column.displayValue(row)
                            : defaultValue(key, raw)}
                        </TableCell>
                      );
                    })}
                    {(onDeactivate || onEdit) && (
                      <TableCell sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
                        <Stack direction="row" spacing={0.75}>
                          {onEdit && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => onEdit(row)}
                              disabled={rowPending}
                            >
                              Edit
                            </Button>
                          )}
                          {onDeactivate && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => onDeactivate(row)}
                              disabled={rowPending}
                            >
                              {rowPending ? 'Deactivating…' : 'Deactivate'}
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default AdminDataTable;
